import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { debounce, set, take, tail, uniqBy, uniq, fromPairs, zipObject, over, filter, flatten, orderBy } from "lodash";
import * as React from "react";
import Avatar from "react-avatar";
import Files from "react-files";
import { Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Pagination, PaginationItem, PaginationLink, Popover, Row } from "reactstrap";
import * as AccountStore from "../account/AccountStore";
import { GetContext, UnsubscribeContext } from "../general/ApplicationContext";
import LoadingSpinner from "../general/Loader";
import * as IdentityPermissionStore from "../identityPermissions/IdentityPermissionStore";
import OrganizationContextSwitcher from "../organizations/ContextSwitcher";
import { MembershipType } from "../organizations/OrganizationStore";
import { IdentityUser, SearchUsers } from "../users/UserStore";
import {
    AddChild,
    AddMember,
    CreateChildGroupRequest,
    CreateGroup,
    CreateGroupMemberRequest,
    CreateGroupRequest,
    CreateGroupType,
    CreateGroupTypeRequest,
    DeleteGroup,
    GetGroup,
    GetGroupCustomTypes,
    GetGroups,
    GetOrganizationMembers,
    GetOrgChart,
    Group,
    GroupCustomType,
    GroupType,
    GroupTypeMap,
    GroupUserMembership,
    OrgChartNode,
    RemoveChild,
    RemoveMember,
} from "./GroupStore";

import styled from "styled-components";
import { GroupTree } from "./GroupTree";
import OrgChart from "./OrgChart";
import ScrollContainer from "../components/ScrollContainer";

let contextSubscriptionId = "";

export const GroupsIndex = () => {
    const [groups, setGroups] = React.useState([] as Group[]);
    const [filteredGroups, setFilteredGroups] = React.useState([] as Group[]);
    const [pages, setPages] = React.useState(0);
    const pageSize = 10;
    const [page, setPage] = React.useState(0);

    const [groupCustomTypes, setGroupCustomTypes] = React.useState([] as GroupCustomType[]);
    const [groupTypeMap, setGroupTypeMap] = React.useState({} as any);

    const [searchQuery, setSearchQuery] = React.useState("");
    const [selectedGroup, setSelectedGroup] = React.useState({} as Group);

    const [organizationId, setOrganizationId] = React.useState("");

    const [selectedGroupMembers, setSelectedGroupMembers] = React.useState([] as GroupUserMembership[]);
    const [filteredSelectedGroupMembers, setFilteredSelectedGroupMembers] = React.useState([] as GroupUserMembership[]);
    const [currentGroupUserFilterQuery, setCurrentGroupUserFilterQuery] = React.useState("");

    const [overview, setOverview] = React.useState(false);
    const [detail, setDetail] = React.useState(true);
    const [orgChart, setOrgChart] = React.useState(false);

    const [loading, setLoading] = React.useState(true);

    const hideTabs = () => {
        setOverview(false);
        setDetail(false);
        setOrgChart(false);
    };

    const [totalMemberships, setTotalMemberships] = React.useState(0);
    const [totalUsers, setTotalUsers] = React.useState(0);

    React.useEffect(() => {
        contextSubscriptionId = GetContext((context) => {
            setOrganizationId(context.currentOrganization.id);
        });
        return () => {
            UnsubscribeContext(contextSubscriptionId);
        };
    }, []);

    React.useEffect(() => {
        if (organizationId) {
            getGroups(groupTypeMap);
            getGroupCustomTypes();
        }
    }, [organizationId, groupTypeMap]);

    const [orgchart, setOrgchart] = React.useState([] as OrgChartNode[]);

    const getGroups = async (groupTypeMap) => {
        setLoading(true);
        let types = Object.keys(groupTypeMap).filter((key) => groupTypeMap[key]);

        let res = await Promise.all([
            GetGroups(organizationId, types.join(","))
                .then((result) => {
                    setGroups(result);
                })
                .catch((response) => {}),
            GetOrgChart(organizationId, types.join(",")).then((res) => {
                setOrgchart(res);
            }),
        ]);

        setLoading(false);

        return res;
    };

    const getGroupCustomTypes = () => {
        GetGroupCustomTypes(organizationId)
            .then((groupTypes) => {
                setGroupCustomTypes(groupTypes);
            })
            .catch((response) => {});
    };

    React.useEffect(() => {
        let types = Object.keys(groupTypeMap).filter((key) => groupTypeMap[key]);
        if (searchQuery.length > 0 || types.length > 0) {
            let filtered = groups.filter((group) => {
                let nameMatch = group.name.toLowerCase().includes(searchQuery.toLowerCase()) || group.data?.description?.toLowerCase()?.includes(searchQuery.toLowerCase());
                let typeMatch = types.length == 0 || types.includes(group.groupCustomType?.id);

                return nameMatch && typeMatch;
            });
            setFilteredGroups(filtered);
            setPages(Math.ceil(filtered.length / pageSize));
            setPage(0);
        } else {
            setFilteredGroups(groups);
            setPages(Math.ceil(groups.length / pageSize));
            setPage(0);
        }
    }, [searchQuery, groups, groupTypeMap]);

    React.useEffect(() => {
        setFilteredSelectedGroupMembers(selectedGroupMembers.filter((m) => m.member?.firstName?.toLowerCase()?.includes(currentGroupUserFilterQuery.toLowerCase())));
    }, [currentGroupUserFilterQuery, selectedGroupMembers]);

    React.useEffect(() => {
        setTotalMemberships(filteredGroups.reduce((a, b) => a + b.groupUsers.length, 0));
        setTotalUsers(uniqBy(flatten(filteredGroups.map((g) => g.groupUsers)), (g) => g.memberID).length);
    }, [filteredGroups]);

    const handleQueryChange = (e) => {
        var query = e.target.value;
        setSearchQuery(query);
    };

    const [showGroupMembersModal, setShowGroupMembersModal] = React.useState(false);
    const [loadingGroupMembers, setLoadingGroupMembers] = React.useState(false);

    const [showChildGroupModal, setShowChildGroupModal] = React.useState(false);

    const startShowGroupMembers = (group: Group) => () => {
        setLoadingGroupMembers(true);
        setSelectedGroup(group);
        setShowGroupMembersModal(true);
        setCurrentGroupUserFilterQuery("");
        GetGroup(organizationId, group.id).then((res) => {
            setSelectedGroup(res);
            setSelectedGroupMembers(orderBy(res.allUsers, ["membershipType", "member.lastName"], ["desc", "asc"]));
            setLoadingGroupMembers(false);
        });
    };

    const [showAddGroupMemberModal, setShowAddGroupMemberModal] = React.useState(false);
    const [showAddGroupOwnerModal, setShowAddGroupOwnerModal] = React.useState(false);

    const startAddGroupMember = () => {
        setShowGroupMembersModal(false);
        setShowAddGroupMemberModal(true);
    };

    const startAddGroupOwner = () => {
        setShowGroupMembersModal(false);
        setShowAddGroupOwnerModal(true);
    };

    const startAddChildGroup = () => {
        setShowGroupMembersModal(false);
        setShowChildGroupModal(true);
    };

    const [userSearchQuery, setUserSearchQuery] = React.useState("");
    const [userSearchResults, setUserSearchResults] = React.useState([] as IdentityUser[]);
    const [userSkip, setUserSkip] = React.useState(0);
    const [userSearchLoading, setUserSearchLoading] = React.useState(false);

    const [groupSearchQuery, setGroupSearchQuery] = React.useState("");
    const [groupSearchResults, setGroupSearchResults] = React.useState([] as Group[]);
    const [groupSkip, setGroupSkip] = React.useState(0);
    const [groupSearchLoading, setGroupSearchLoading] = React.useState(false);

    const searchUsers = () => {
        setUserSearchLoading(true);
        SearchUsers(userSearchQuery, userSkip, 10).then((res) => {
            setUserSearchResults(res.data);
            setUserSearchLoading(false);
        });
    };

    const searchGroups = () => {
        setGroupSearchLoading(true);
        GetGroups(organizationId, "", groupSearchQuery).then((res) => {
            setGroupSearchResults(res);
            setGroupSearchLoading(false);
        });
    };

    const addUserToGroup = (user: IdentityUser, role: string) => () => {
        AddMember(organizationId, selectedGroup.id, { userID: user.id, type: role == "member" ? 0 : 1 } as CreateGroupMemberRequest).then((res) => {
            setShowAddGroupMemberModal(false);
            setShowAddGroupOwnerModal(false);
            startShowGroupMembers(selectedGroup)();
            getGroups(groupTypeMap);
        });
    };

    const removeUserFromGroup = (user: IdentityUser) => () => {
        RemoveMember(organizationId, selectedGroup.id, user.id).then((res) => {
            startShowGroupMembers(selectedGroup)();
            getGroups(groupTypeMap);
        });
    };

    const addChildGroupToGroup = (parent: Group, child: Group) => {
        AddChild(organizationId, parent.id, { groupID: child.id } as CreateChildGroupRequest).then((res) => {
            setShowChildGroupModal(false);
            startShowGroupMembers(selectedGroup)();
            getGroups(groupTypeMap);
        });
    };

    const removeChildFromGroup = (parent: Group, child: Group) => () => {
        RemoveChild(organizationId, parent.id, child.id).then((res) => {
            startShowGroupMembers(selectedGroup)();
            getGroups(groupTypeMap);
        });
    };

    const [showAddGroupModal, setShowAddGroupModal] = React.useState(false);
    const [groupName, setGroupName] = React.useState("");
    const [groupCustomTypeId, setGroupCustomTypeId] = React.useState("");
    const [groupType, setGroupType] = React.useState(0 as GroupType);

    const startAddGroup = () => {
        setShowAddGroupModal(true);
        setGroupName("");
        setGroupCustomTypeId("");
        setGroupType(0);
    };

    const [showAddGroupTypeModal, setShowAddGroupTypeModal] = React.useState(false);
    const [groupTypeName, setGroupTypeName] = React.useState("");

    const startAddGroupType = () => {
        setShowAddGroupTypeModal(true);
        setGroupTypeName("");
    };

    const createGroup = () => {
        CreateGroup(organizationId, { name: groupName, typeID: groupCustomTypeId } as CreateGroupRequest).then((res) => {
            if (res.success) {
                getGroups(groupTypeMap);
                setShowAddGroupModal(false);
            }
        });
    };

    const createGroupType = () => {
        CreateGroupType(organizationId, { name: groupTypeName } as CreateGroupTypeRequest).then((res) => {
            if (res.success) {
                getGroupCustomTypes();
                setShowAddGroupTypeModal(false);
            }
        });
    };

    const [showDeleteGroupModal, setShowDeleteGroupModal] = React.useState(false);
    const [groupToDelete, setGroupToDelete] = React.useState({} as Group);

    const startDeleteGroup = (group: Group) => () => {
        setGroupToDelete(group);
        setShowDeleteGroupModal(true);
    };

    const deleteGroup = () => {
        DeleteGroup(groupToDelete.id).then((res) => {
            getGroups(groupTypeMap);
            setShowDeleteGroupModal(false);
        });
    };

    return (
        <div>
            <OrganizationContextSwitcher></OrganizationContextSwitcher>
            <div className="form-group row p-2">
                <div className="col-xs-12 col-sm-3">
                    <input type="text" className="form-control" name="search" value={searchQuery} onChange={handleQueryChange} placeholder="Search" />
                </div>
                <div className="col">
                    <Button outline color="primary" onClick={startAddGroup}>
                        <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon> Add Group
                    </Button>
                    <Button style={{ marginLeft: "10px" }} outline color="primary" onClick={startAddGroupType}>
                        <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon> Add Group Type
                    </Button>
                </div>
            </div>
            <div className="row p-2">
                {groupCustomTypes.map((groupCustomType) => (
                    <Col key={groupCustomType.id}>
                        <FormGroup check>
                            <Input
                                type="switch"
                                label=""
                                id={groupCustomType.id}
                                checked={groupTypeMap[groupCustomType.id]}
                                onChange={(e) => setGroupTypeMap({ ...groupTypeMap, [groupCustomType.id]: e.target.checked })}
                            />
                            <Label for={groupCustomType.id} check>
                                {groupCustomType.name}
                            </Label>
                        </FormGroup>
                    </Col>
                ))}
            </div>
            {loading && <LoadingSpinner loading={loading}></LoadingSpinner>}
            {!loading && (
                <div className="d-flex justify-content-center" style={{ gap: "1rem" }}>
                    <Button
                        outline={!overview}
                        color="primary"
                        onClick={() => {
                            hideTabs();
                            setOverview(true);
                        }}
                    >
                        Overview
                    </Button>
                    <Button
                        outline={!detail}
                        color="primary"
                        onClick={() => {
                            hideTabs();
                            setDetail(true);
                        }}
                    >
                        Detail
                    </Button>
                    <Button
                        outline={!orgChart}
                        color="primary"
                        onClick={() => {
                            hideTabs();
                            setOrgChart(true);
                        }}
                    >
                        Org Chart
                    </Button>
                </div>
            )}
            {overview && (
                <>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr" }}>
                        {filteredGroups.map((group, i) => (
                            <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr" }} key={i} className="p-1">
                                <span>
                                    {group.name} {group.data?.description}
                                </span>
                                {group.type != 1 && <span>{GroupTypeMap[group.type] || "-"}</span>}
                                {group.type == 1 && <span>{group.groupCustomType?.name || "-"}</span>}
                                <span>{group.groupUsers.length}</span>
                            </div>
                        ))}
                    </div>
                    <div>
                        <strong>
                            Total Memberships {totalMemberships} ({totalUsers} Users)
                        </strong>
                    </div>
                </>
            )}
            {detail && (
                <>
                    <table className="table">
                        <thead>
                            <tr>
                                <th style={{ width: "5%" }}></th>
                                <th style={{ width: "40%" }}>Name</th>
                                <th style={{ width: "40%" }}>Type</th>
                                <th style={{ width: "20%" }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredGroups.slice(page * 10, (page + 1) * 10).map((group, i) => (
                                <tr key={i}>
                                    <td>
                                        <Avatar name={group.name} size="50px" round={true} />
                                    </td>
                                    <td>
                                        <div>
                                            {group.name} {group.data?.description}
                                        </div>
                                        <small className="text-muted">{group.id}</small>
                                    </td>
                                    <td>
                                        <div>{GroupTypeMap[group.type] || "-"}</div>
                                        <div>{group.groupCustomType?.name || "-"}</div>
                                    </td>
                                    <td>
                                        <Button color="success" style={{ marginLeft: "1rem" }} outline onClick={startShowGroupMembers(group)}>
                                            Members
                                        </Button>
                                        <Button color="danger" style={{ marginLeft: "1rem" }} outline onClick={startDeleteGroup(group)}>
                                            Delete
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <Pagination>
                        {Array.from(Array(pages).keys()).map((i) => (
                            <PaginationItem key={i} active={page == i}>
                                <PaginationLink href="#" onClick={() => setPage(i)}>
                                    {i + 1}
                                </PaginationLink>
                            </PaginationItem>
                        ))}
                    </Pagination>
                </>
            )}
            {orgChart && (
                <>
                    <OrgChart groups={orgchart}></OrgChart>
                    {/* <GroupTree groups={orgchart}></GroupTree> */}
                </>
            )}
            <Modal isOpen={showGroupMembersModal} toggle={() => setShowGroupMembersModal(false)} centered={true}>
                <ModalHeader toggle={() => setShowGroupMembersModal(false)}>
                    {selectedGroup.name} Members - {selectedGroup?.groupCustomType?.name}
                </ModalHeader>
                <ModalBody>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="User"
                        aria-label="User"
                        value={currentGroupUserFilterQuery}
                        onChange={(e) => setCurrentGroupUserFilterQuery(e.target.value)}
                    />

                    <ScrollContainer direction={"-y"} style={{ maxHeight: "80vh" }}>
                        {loadingGroupMembers && <LoadingSpinner></LoadingSpinner>}
                        {filteredSelectedGroupMembers.map((membership) => (
                            <div key={membership.member.id} className="d-flex justify-content-between my-2">
                                <div className="d-flex ">
                                    <Avatar name={membership.member.firstName} size="50px" round={true} />
                                    <span style={{ marginLeft: "1rem" }}>
                                        {membership.member.firstName} {membership.member.lastName}
                                        {membership.membershipType == 1 && (
                                            <>
                                                <br />
                                                <span className="text-muted">Owner</span>
                                            </>
                                        )}
                                        <br />
                                        <span className="text-muted">{membership.member.email}</span>
                                        <br />
                                        {selectedGroup.groupUsers.filter((u) => u.memberID == membership.memberID).length == 0 && (
                                            <span className="text-muted">
                                                Member of: {selectedGroup.childGroups.filter((c) => c.groupUsers.some((s) => s.memberID == membership.memberID))[0]?.name}
                                            </span>
                                        )}
                                    </span>
                                </div>
                                <div>
                                    {selectedGroup.type != 5 && selectedGroup.groupUsers.filter((u) => u.memberID == membership.memberID).length > 0 && (
                                        <Button color="danger" style={{ marginLeft: "1rem" }} outline onClick={removeUserFromGroup(membership.member)}>
                                            Remove
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                        {selectedGroup.childGroups?.map((group) => (
                            <div key={group.id} className="d-flex justify-content-between my-2">
                                <div className="d-flex ">
                                    <Avatar name={group.name} size="50px" round={true} />
                                    <span style={{ marginLeft: "1rem" }}>
                                        {group.name}
                                        <br />
                                        <span className="text-muted">Group</span>
                                        <br />
                                        <span className="text-muted">{group.groupCustomType?.name}</span>
                                    </span>
                                </div>
                                <div>
                                    <Button color="danger" style={{ marginLeft: "1rem" }} outline onClick={removeChildFromGroup(selectedGroup, group)}>
                                        Remove
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </ScrollContainer>
                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="secondary" onClick={() => setShowGroupMembersModal(false)}>
                        Close
                    </Button>
                    {selectedGroup.type != 5 && (
                        <>
                            <Button type="button" color="primary" onClick={() => startAddChildGroup()}>
                                Add Group
                            </Button>
                            <Button type="button" color="primary" onClick={() => startAddGroupMember()}>
                                Add Member
                            </Button>
                            <Button type="button" color="success" onClick={() => startAddGroupOwner()}>
                                Add Owner
                            </Button>
                        </>
                    )}
                </ModalFooter>
            </Modal>
            <Modal isOpen={showAddGroupMemberModal} toggle={() => setShowAddGroupMemberModal(false)} centered={true}>
                <ModalHeader toggle={() => setShowAddGroupMemberModal(false)}>Add Member to {selectedGroup.name}</ModalHeader>
                <ModalBody>
                    <div className="input-group mb-3">
                        <input type="text" className="form-control" placeholder="User" aria-label="User" value={userSearchQuery} onChange={(e) => setUserSearchQuery(e.target.value)} />
                        <div className="input-group-append">
                            <button className="btn btn-outline-secondary" type="button" onClick={searchUsers}>
                                Search
                            </button>
                        </div>
                    </div>
                    <ScrollContainer direction={"-y"} style={{ maxHeight: "80vh" }}>
                        {userSearchLoading && <LoadingSpinner></LoadingSpinner>}
                        {userSearchResults.map((user) => (
                            <div key={user.id} className="d-flex justify-content-between my-2">
                                <div className="d-flex ">
                                    <Avatar name={user.firstName} size="50px" round={true} />
                                    <span style={{ marginLeft: "1rem" }}>
                                        {user.firstName} {user.lastName}
                                        <br />
                                        <span className="text-muted">{user.email}</span>
                                    </span>
                                </div>
                                <div>
                                    <Button color="primary" style={{ marginLeft: "1rem" }} outline onClick={addUserToGroup(user, "member")}>
                                        Add
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </ScrollContainer>
                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="secondary" onClick={() => setShowAddGroupMemberModal(false)}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showAddGroupOwnerModal} toggle={() => setShowAddGroupOwnerModal(false)} centered={true}>
                <ModalHeader toggle={() => setShowAddGroupOwnerModal(false)}>Add Owner to {selectedGroup.name}</ModalHeader>
                <ModalBody>
                    <div className="input-group mb-3">
                        <input type="text" className="form-control" placeholder="User" aria-label="User" value={userSearchQuery} onChange={(e) => setUserSearchQuery(e.target.value)} />
                        <div className="input-group-append">
                            <button className="btn btn-outline-secondary" type="button" onClick={searchUsers}>
                                Search
                            </button>
                        </div>
                    </div>
                    <ScrollContainer direction={"-y"} style={{ maxHeight: "80vh" }}>
                        {userSearchLoading && <LoadingSpinner></LoadingSpinner>}
                        {userSearchResults.map((user) => (
                            <div key={user.id} className="d-flex justify-content-between my-2">
                                <div className="d-flex ">
                                    <Avatar name={user.firstName} size="50px" round={true} />
                                    <span style={{ marginLeft: "1rem" }}>
                                        {user.firstName} {user.lastName}
                                        <br />
                                        <span className="text-muted">{user.email}</span>
                                    </span>
                                </div>
                                <div>
                                    <Button color="primary" style={{ marginLeft: "1rem" }} outline onClick={addUserToGroup(user, "owner")}>
                                        Add
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </ScrollContainer>
                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="secondary" onClick={() => setShowAddGroupOwnerModal(false)}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showChildGroupModal} toggle={() => setShowChildGroupModal(false)} centered={true}>
                <ModalHeader toggle={() => setShowChildGroupModal(false)}>Add Group to {selectedGroup.name}</ModalHeader>
                <ModalBody>
                    <div className="input-group mb-3">
                        <input type="text" className="form-control" placeholder="User" aria-label="User" value={groupSearchQuery} onChange={(e) => setGroupSearchQuery(e.target.value)} />
                        <div className="input-group-append">
                            <button className="btn btn-outline-secondary" type="button" onClick={searchGroups}>
                                Search
                            </button>
                        </div>
                    </div>
                    <ScrollContainer direction={"-y"} style={{ maxHeight: "80vh" }}>
                        {groupSearchLoading && <LoadingSpinner></LoadingSpinner>}
                        {groupSearchResults.map((group) => (
                            <div key={group.id} className="d-flex justify-content-between my-2">
                                <div className="d-flex ">
                                    <Avatar name={group.name} size="50px" round={true} />
                                    <span style={{ marginLeft: "1rem" }}>
                                        {group.name}
                                        <br />
                                        <span className="text-muted">{group.groupCustomType?.name}</span>
                                    </span>
                                </div>
                                <div>
                                    <Button color="primary" style={{ marginLeft: "1rem" }} outline onClick={() => addChildGroupToGroup(selectedGroup, group)}>
                                        Add
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </ScrollContainer>
                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="secondary" onClick={() => setShowAddGroupOwnerModal(false)}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showAddGroupModal} toggle={() => setShowAddGroupModal(false)} centered={true}>
                <ModalHeader toggle={() => setShowAddGroupModal(false)}>Add Group</ModalHeader>
                <ModalBody>
                    <div className="form-group row p-2">
                        <div className="col-xs-12">
                            <input
                                type="text"
                                className="form-control"
                                value={groupName}
                                onChange={(e) => {
                                    setGroupName(e.target.value);
                                }}
                                placeholder="Group Name"
                            />
                        </div>
                        <div className="col-xs-12 mt-2">
                            <Input type="select" name="select" id="exampleSelect" onChange={(e) => setGroupCustomTypeId(e.target.value)}>
                                <option value="">Select Group Type</option>
                                {groupCustomTypes.map((groupCustomType) => (
                                    <option key={groupCustomType.id} value={groupCustomType.id}>
                                        {groupCustomType.name}
                                    </option>
                                ))}
                            </Input>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="secondary" onClick={() => setShowAddGroupModal(false)}>
                        Close
                    </Button>
                    <Button type="button" color="primary" onClick={() => createGroup()}>
                        Create
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showAddGroupTypeModal} toggle={() => setShowAddGroupTypeModal(false)} centered={true}>
                <ModalHeader toggle={() => setShowAddGroupTypeModal(false)}>Add Group Type</ModalHeader>
                <ModalBody>
                    <div className="form-group row p-2">
                        <div className="col-xs-12">
                            <input
                                type="text"
                                className="form-control"
                                value={groupTypeName}
                                onChange={(e) => {
                                    setGroupTypeName(e.target.value);
                                }}
                                placeholder="Group Type Name"
                            />
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="secondary" onClick={() => setShowAddGroupTypeModal(false)}>
                        Close
                    </Button>
                    <Button type="button" color="primary" onClick={() => createGroupType()}>
                        Create
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showDeleteGroupModal} toggle={() => setShowDeleteGroupModal(false)} centered={true}>
                <ModalHeader toggle={() => setShowDeleteGroupModal(false)}>
                    Delete {selectedGroup.name} - {selectedGroup?.groupCustomType?.name}
                </ModalHeader>
                <ModalBody>Are you sure you want to delete {selectedGroup.name}? This action cannot be undone. All members will be removed</ModalBody>
                <ModalFooter>
                    <Button type="button" color="secondary" onClick={() => setShowDeleteGroupModal(false)}>
                        Close
                    </Button>
                    <Button type="button" color="danger" onClick={() => deleteGroup()}>
                        Delete
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};
